/**
 * Plan Question Parser
 * Parses questions from AI responses in multiple formats for plan mode
 */

export interface PlanQuestion {
  id: string;
  question: string;
  options: string[];
  allowMultiple?: boolean; // Configurable per question
  required?: boolean;
}

/**
 * Parse questions from EXTRA object format
 * Format: { EXTRA: { plan_questions: [{ id, question, options, allowMultiple }] } }
 */
function parseFromExtra(data: any): PlanQuestion[] {
  if (!data?.EXTRA?.plan_questions) {
    return [];
  }

  const questions = data.EXTRA.plan_questions;
  if (!Array.isArray(questions)) {
    return [];
  }

  return questions
    .filter((q: any) => q && q.question && Array.isArray(q.options) && q.options.length > 0)
    .map((q: any, index: number) => ({
      id: q.id || `question-${index}-${Date.now()}`,
      question: String(q.question || '').trim(),
      options: q.options.map((opt: any) => String(opt).trim()).filter((opt: string) => opt.length > 0),
      allowMultiple: q.allowMultiple === true || q.allowMultiple === 'true',
      required: q.required !== false, // Default to true unless explicitly false
    }))
    .filter((q: PlanQuestion) => q.question.length > 0 && q.options.length > 0);
}

/**
 * Parse questions from markdown format
 * Looks for patterns like:
 * ## Questions:
 * 1. Question text?
 *    - Option 1
 *    - Option 2
 * 
 * Or:
 * **Question:** Question text?
 * - Option 1
 * - Option 2
 */
function parseFromMarkdown(responseText: string): PlanQuestion[] {
  if (!responseText || typeof responseText !== 'string') {
    return [];
  }

  const questions: PlanQuestion[] = [];
  const lines = responseText.split('\n');
  
  let currentQuestion: Partial<PlanQuestion> | null = null;
  let inQuestionBlock = false;
  let questionIndex = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Detect question header
    if (line.match(/^##+\s*(?:Questions?|Q&A)/i) || line.match(/^\*\*Questions?:\*\*/i)) {
      inQuestionBlock = true;
      continue;
    }

    // Detect numbered question (1., 2., etc.) or **Question:**
    const numberedMatch = line.match(/^(\d+)\.\s*(.+[?])/i);
    const boldMatch = line.match(/^\*\*Questions?:\*\*\s*(.+[?])/i);
    
    if (numberedMatch || boldMatch) {
      // Save previous question if exists
      if (currentQuestion && currentQuestion.question && currentQuestion.options && currentQuestion.options.length > 0) {
        questions.push({
          id: currentQuestion.id || `question-${questionIndex}-${Date.now()}`,
          question: currentQuestion.question,
          options: currentQuestion.options,
          allowMultiple: currentQuestion.allowMultiple || false,
          required: currentQuestion.required !== false,
        });
        questionIndex++;
      }
      
      // Start new question
      const questionText = numberedMatch ? numberedMatch[2] : boldMatch![1];
      currentQuestion = {
        id: `question-${questionIndex}-${Date.now()}`,
        question: questionText.trim(),
        options: [],
        allowMultiple: false,
        required: true,
      };
      continue;
    }

    // Detect options (lines starting with -, *, or •)
    if (currentQuestion && (line.match(/^[-*•]\s*(.+)/) || line.match(/^\d+\)\s*(.+)/))) {
      const optionMatch = line.match(/^[-*•]\s*(.+)/) || line.match(/^\d+\)\s*(.+)/);
      if (optionMatch && optionMatch[1]) {
        const optionText = optionMatch[1].trim();
        if (optionText.length > 0) {
          currentQuestion.options = currentQuestion.options || [];
          currentQuestion.options.push(optionText);
        }
      }
    }
  }

  // Save last question
  if (currentQuestion && currentQuestion.question && currentQuestion.options && currentQuestion.options.length > 0) {
    questions.push({
      id: currentQuestion.id || `question-${questionIndex}-${Date.now()}`,
      question: currentQuestion.question,
      options: currentQuestion.options,
      allowMultiple: currentQuestion.allowMultiple || false,
      required: currentQuestion.required !== false,
    });
  }

  return questions;
}

/**
 * Parse questions from JSON embedded in response text
 * Looks for JSON objects like: {"plan_questions": [...]}
 */
function parseFromJsonInText(responseText: string): PlanQuestion[] {
  if (!responseText || typeof responseText !== 'string') {
    return [];
  }

  // Try to find JSON objects in the text
  const jsonMatches = responseText.match(/\{[\s\S]*?"plan_questions"[\s\S]*?\}/g);
  if (!jsonMatches || jsonMatches.length === 0) {
    return [];
  }

  for (const match of jsonMatches) {
    try {
      const parsed = JSON.parse(match);
      if (parsed.plan_questions && Array.isArray(parsed.plan_questions)) {
        return parsed.plan_questions
          .filter((q: any) => q && q.question && Array.isArray(q.options) && q.options.length > 0)
          .map((q: any, index: number) => ({
            id: q.id || `question-${index}-${Date.now()}`,
            question: String(q.question || '').trim(),
            options: q.options.map((opt: any) => String(opt).trim()).filter((opt: string) => opt.length > 0),
            allowMultiple: q.allowMultiple === true || q.allowMultiple === 'true',
            required: q.required !== false,
          }))
          .filter((q: PlanQuestion) => q.question.length > 0 && q.options.length > 0);
      }
    } catch (e) {
      // Invalid JSON, continue to next match
      continue;
    }
  }

  return [];
}

/**
 * Main parser function - tries all formats
 */
export function parsePlanQuestions(data: any, mode?: string): PlanQuestion[] {
  // Only parse if mode is 'plan' or if EXTRA.plan_questions exists
  if (mode !== 'plan' && !data?.EXTRA?.plan_questions) {
    return [];
  }

  const questions: PlanQuestion[] = [];

  // Try EXTRA object format first (most reliable)
  const extraQuestions = parseFromExtra(data);
  if (extraQuestions.length > 0) {
    return extraQuestions;
  }

  // Try parsing from response text
  const responseText = typeof data.response === 'string' ? data.response : JSON.stringify(data.response || '');
  
  // Try JSON in text
  const jsonQuestions = parseFromJsonInText(responseText);
  if (jsonQuestions.length > 0) {
    return jsonQuestions;
  }

  // Try markdown format
  const markdownQuestions = parseFromMarkdown(responseText);
  if (markdownQuestions.length > 0) {
    return markdownQuestions;
  }

  return questions;
}

/**
 * Format selected answers for submission to AI
 */
export function formatAnswersForSubmission(
  questions: PlanQuestion[],
  selectedAnswers: Record<string, string[]>
): string {
  const answers: string[] = [];

  questions.forEach((question) => {
    const selections = selectedAnswers[question.id] || [];
    if (selections.length > 0) {
      const answerText = selections.join(', ');
      answers.push(`Q: ${question.question}\nA: ${answerText}`);
    }
  });

  if (answers.length === 0) {
    return 'No answers provided.';
  }

  return `Here are my answers to your questions:\n\n${answers.join('\n\n')}`;
}















